import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { concatMap, map, take } from 'rxjs/operators';

import { GroupsStoreService } from '@libs/src/groups/groups-store/groups-store.service';

import type { NavigationLink } from './group-view-navigation.types';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { IAppState } from '@main-client/src/app/app.state';
import { EVENT_FILTER_TYPES } from '@main-client/src/app/events/events.helpers';
import { NEWS_ROUTE_PATHS } from '@main-client/src/app/news/news.helpers';
import {
  isAdminOfGroup,
  isAdminOfPrograms,
  isTenThousandCoffeesAdmin,
} from '@main-client/src/app/user/user.helpers';

@Injectable()
export class GroupViewNavigationService {
  constructor(
    private readonly groupsStoreService: GroupsStoreService,
    private readonly store: Store<IAppState>,
  ) {}

  generateNavLinks({
    group,
    isManagingPathwayForGroup,
    isManagingProgramForGroup,
    tenant,
    user,
  }: {
    group: Group;
    isManagingPathwayForGroup: boolean;
    isManagingProgramForGroup: boolean;
    tenant: Tenant;
    user: Account;
  }) {
    const baseHubLinkSegments = ['/hub', group.slug];
    const isAdmin = user.isAdmin || isAdminOfGroup(user, group);
    const isDevelopmentProgramAccessible =
      isAdminOfPrograms(user, group) ||
      isManagingProgramForGroup ||
      isManagingPathwayForGroup;
    const isDataEnabled = tenant.enabled_features.data;
    const is10kcAdmin = isTenThousandCoffeesAdmin(user);
    const groupMembership = getMembership(user.groups, group._id);
    const hasCommunityPageEnabled =
      group.community_page_information?.is_visible &&
      (isAdmin || groupMembership.community_page_enabled);
    return [
      ...(hasCommunityPageEnabled
        ? [
            {
              routerLink: [...baseHubLinkSegments, 'community'],
              title: $localize`:@@groupViewNavigation.welcome:Welcome`,
            },
          ]
        : []),
      {
        routerLink: [...baseHubLinkSegments, 'experiences'],
        title: $localize`:@@groupViewNavigation.navLinkLabel:My experiences`,
      },
      ...((isAdmin || isManagingPathwayForGroup) &&
      tenant?.enabled_features?.pathways &&
      group.features_enabled?.pathways
        ? [
            this.getPathwaysStates(
              baseHubLinkSegments,
              isAdmin || isManagingPathwayForGroup,
            ),
          ]
        : []),
      ...(false === group.features_enabled?.introductions
        ? []
        : [this.getIntrosStates(baseHubLinkSegments, isAdmin)]),
      ...(group.development_programs_enabled
        ? [
            this.getDevelopmentProgramsStates(
              baseHubLinkSegments,
              isAdmin || isDevelopmentProgramAccessible,
            ),
          ]
        : []),
      ...(isAdmin || (tenant?.enabled_features?.members_directory ?? true)
        ? [this.getMembersState(baseHubLinkSegments, isAdmin, user.isAdmin)]
        : []),
      ...(isAdmin
        ? [
            this.getInviteMembersState(
              baseHubLinkSegments,
              group?.invitation_email_campaigns_enabled,
            ),
          ]
        : []),
      ...((tenant?.enabled_features?.events ?? true) &&
      (group?.features_enabled?.events ?? true)
        ? [
            this.getEventsStates(
              baseHubLinkSegments,
              isAdmin,
              isManagingPathwayForGroup,
            ),
          ]
        : []),
      ...(tenant.enabled_features && tenant.enabled_features.news
        ? [this.getNewsStates(baseHubLinkSegments, isAdmin)]
        : []),
      ...(isAdmin && isDataEnabled
        ? [
            {
              routerLink: [...baseHubLinkSegments, 'data'],
              title: $localize`:@@groupViewNavigation.dataAndReporting:Data & Reporting`,
            },
          ]
        : []),
      ...(is10kcAdmin
        ? [
            {
              routerLink: [...baseHubLinkSegments, 'email-communications'],
              shouldAsterisk: true,
              title: $localize`:@@groupViewNavigation.emailCommunications:Email communications`,
            },
          ]
        : []),
      ...this.getHubSettingsStates(baseHubLinkSegments, isAdmin, tenant, group),
    ];
  }

  getMembersState(
    baseSegments: string[],
    isAnyAdmin: boolean,
    isRootAdmin?: boolean,
  ): NavigationLink {
    return {
      routerLink: [...baseSegments, 'members'],
      subStates: isAnyAdmin
        ? [
            {
              labelSource: this.store.select('currentGroup').pipe(
                take(1),
                concatMap((groupId: string) =>
                  this.groupsStoreService.getGroupFromStore({ _id: groupId }),
                ),
                map(({ joinRequestsCount }) =>
                  0 === joinRequestsCount ? '' : `${joinRequestsCount}`,
                ),
              ),
              routerLink: [...baseSegments, 'manage-members', 'join-requests'],
              title: $localize`:@@groupViewNavigation.members.joinRequests:Join requests`,
            },
            {
              routerLink: [...baseSegments, 'manage-members', 'members'],
              title: $localize`:@@global.manageMembers:Manage members`,
            },
            {
              routerLink: [...baseSegments, 'manage-members', 'admins'],
              title: $localize`:@@groupViewNavigation.members.manageAdmins:Admins`,
            },
            ...(isRootAdmin
              ? [
                  {
                    routerLink: [
                      ...baseSegments,
                      'manage-members',
                      'new-members',
                    ],
                    title: $localize`:@@groupViewNavigation.members.addExisting:Add existing members`,
                  },
                ]
              : []),
          ]
        : [],
      title: $localize`:@@groupViewNavigation.members:Members`,
    };
  }

  getNewsStates(baseSegments: string[], isAdmin: boolean) {
    return {
      routerLink: [...baseSegments, 'articles', NEWS_ROUTE_PATHS.upcoming],
      subStates: [
        ...(isAdmin
          ? [
              {
                routerLink: [
                  ...baseSegments,
                  'articles',
                  NEWS_ROUTE_PATHS.drafts,
                ],
                title: $localize`:@@groupViewNavigation.news.drafts:Drafts`,
              },
            ]
          : []),
        {
          routerLink: [...baseSegments, 'articles', NEWS_ROUTE_PATHS.past],
          title: $localize`:@@groupViewNavigation.news.past:Past`,
        },
      ],
      title: $localize`:@@groupViewNavigation.news:News`,
    };
  }

  getDevelopmentProgramsStates(
    baseSegments: string[],
    enableSubStates: boolean,
  ) {
    return {
      routerLink: [...baseSegments, 'programs'],
      subStates: [
        ...(enableSubStates
          ? [
              {
                routerLink: [...baseSegments, 'manage-programs', 'programs'],
                title: $localize`:@@groupViewNavigation.developmentPrograms.manage:Manage programs`,
              },
            ]
          : []),
      ],
      title: $localize`:@@groupViewNavigation.developmentPrograms:Development programs`,
    };
  }

  getInviteMembersState(
    baseSegments: string[],
    isInvitationEmailCampaignsEnabled: boolean,
  ) {
    return {
      routerLink: [...baseSegments, 'manage-members', 'invites'],
      subStates: [
        {
          routerLink: [...baseSegments, 'manage-members', 'sent-invites'],
          title: $localize`:@@groupViewNavigation.inviteMembers.sent:Sent invites`,
        },
        ...(isInvitationEmailCampaignsEnabled
          ? [
              {
                routerLink: [
                  ...baseSegments,
                  'manage-members',
                  'invitation-email-campaigns',
                ],
                title: $localize`:@@groupViewNavigation.inviteMembers.invitationEmails:Invitation emails`,
              },
            ]
          : []),
      ],
      title: $localize`:@@groupViewNavigation.inviteMembers:Invite members`,
    };
  }

  getEventsStates(
    baseSegments: string[],
    isAdmin: boolean,
    isPathwayManager: boolean,
  ) {
    return {
      routerLink: [...baseSegments, 'events', EVENT_FILTER_TYPES.upcoming],
      subStates: [
        ...(isAdmin || isPathwayManager
          ? [
              {
                routerLink: [
                  ...baseSegments,
                  'events',
                  EVENT_FILTER_TYPES.draft,
                ],
                title: $localize`:@@groupViewNavigation.officeHours.drafts:Drafts`,
              },
            ]
          : []),
        {
          routerLink: [...baseSegments, 'events', EVENT_FILTER_TYPES.requested],
          title: $localize`:@@groupViewNavigation.officeHours.pendingApproval:Pending approval`,
        },
        {
          routerLink: [...baseSegments, 'events', EVENT_FILTER_TYPES.past],
          title: $localize`:@@groupViewNavigation.officeHours.past:Past`,
        },
        {
          routerLink: [...baseSegments, 'events', EVENT_FILTER_TYPES.rsvps],
          title: $localize`:@@groupViewNavigation.officeHours.registrations:Registrations`,
        },
        ...(isAdmin
          ? [
              {
                routerLink: [...baseSegments, 'events-settings'],
                title: $localize`:@@groupViewNavigation.officeHours.settings:Settings`,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                routerLink: [...baseSegments, 'events-engagement-campaigns'],
                title: $localize`:@@groupViewNavigation.engagementCampaigns:Engagement campaigns`,
              },
            ]
          : []),
      ],
      title: $localize`:@@groupViewNavigation.officeHours:Office Hours`,
    };
  }

  getIntrosStates(baseSegments: string[], isAdmin: boolean) {
    const introFeedbackDataState = {
      routerLink: [...baseSegments, 'manage-introductions', 'feedbacks'],
      title: $localize`:@@groupViewNavigation.introductions.feedback:Feedback`,
    };
    const subStates = isAdmin
      ? [
          {
            routerLink: [...baseSegments, 'manage-introductions', 'dashboard'],
            title: $localize`:@@groupViewNavigation.introductions.dashboard:Dashboard`,
          },
          {
            routerLink: [
              ...baseSegments,
              'manage-introductions',
              'introductions-schedule',
            ],
            title: $localize`:@@groupViewNavigation.introductions.schedule:Schedule`,
          },
          {
            routerLink: [
              ...baseSegments,
              'manage-introductions',
              'introductions-configurations',
            ],
            title: $localize`:@@groupViewNavigation.introductions.configurations:Configurations`,
          },
          introFeedbackDataState,
        ]
      : [];

    return {
      subStates,
      routerLink: [...baseSegments, 'introductions'],
      title: $localize`:@@groupViewNavigation.introductions:Introductions`,
    };
  }

  getHubSettingsStates(
    baseSegments: string[],
    isAdmin: boolean,
    tenant: Tenant,
    group: Group,
  ) {
    if (!isAdmin) {
      return [];
    }
    const subStates = [];
    subStates.push({
      routerLink: [
        ...baseSegments,
        'manage-hub-membership',
        'membership-configurations',
      ],
      title: $localize`:@@groupViewNavigation.hubSettings.membership:Membership configurations`,
    });
    if (
      tenant.enabled_features?.localization &&
      group.features_enabled?.localization
    ) {
      subStates.push({
        routerLink: [...baseSegments, 'manage-hub-localization'],
        title: $localize`:@@groupViewNavigation.hubSettings.localization:Hub localization`,
      });
    }
    subStates.push({
      routerLink: [
        ...baseSegments,
        'manage-community-page',
        'community-page-configurations',
      ],
      title: $localize`:@@groupViewNavigation.hubSettings.welcomePage:Edit Welcome page`,
    });
    return [
      {
        subStates,
        routerLink: [...baseSegments, 'manage-settings'],
        title: $localize`:@@groupViewNavigation.hubSettings:Hub settings`,
      },
    ];
  }

  getPathwaysStates(baseSegments: string[], enableSubStates: boolean) {
    return {
      routerLink: [...baseSegments, 'pathways'],
      subStates: [
        ...(enableSubStates
          ? [
              {
                routerLink: [...baseSegments, 'manage-pathways'],
                title: $localize`:@@global.managePathways:Manage Pathways`,
              },
            ]
          : []),
      ],
      title: $localize`:@@global.pathways:Pathways`,
    };
  }
}
